import clsx from 'clsx';
import { Download, FileUp, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { COMPANY_STATUSES, type CompanyStatus } from '@pz/core';
import type { ProcOutput } from '@pz/server';
import {
  CompanyDialog,
  CompanyStatusPill,
  ContactDialog,
  ImportDialog,
  TermsDialog,
  useContactsExport,
  usePaymentLine,
  type CompanyRow,
  type ContactRow,
} from '../components/customers';
import { StepUpSettings } from '../components/step-up';
import { Button, Checkbox, EmptyState, ErrorBox, Input, Loading, matchesSearch, PageHeader, Pill, Select, SortTh, Stat, Tabs, th, useToast } from '../components/ui';
import { useTableSort } from '../lib/sort';
import { VirtualTable, ScrollBox, cell, clip } from '../components/virtual';
import { hh, money } from '../lib/format';
import { useM, useQ, useTabParam } from '../lib/hooks';
import { useMe } from '../lib/me';
import { useSticky } from '../lib/sticky';

type Tab = 'contacts' | 'companies' | 'terms' | 'migration';
const TABS: readonly Tab[] = ['contacts', 'companies', 'terms', 'migration'];

interface Filters {
  search: string;
  setSearch: (v: string) => void;
  status: CompanyStatus | '';
  setStatus: (v: CompanyStatus | '') => void;
}

/**
 * Customers: the contact list of the team (one row per contact with the data of the company and the customer status),
 * the companies behind it, the payment terms of the projects and the takeover of the free texts "Kunde" and "Partner".
 * Only for the right "Kundenstamm" (management).
 */
export function CustomersPage() {
  const { t } = useTranslation();
  const { can } = useMe();
  const [tab, setTab] = useTabParam<Tab>(TABS);
  const [search, setSearch] = useSticky('customers.search', '');
  const [status, setStatus] = useSticky<CompanyStatus | ''>('customers.status', '');
  if (!can('customers.manage')) return <EmptyState>{t('customers.noAccess')}</EmptyState>;
  const filters: Filters = { search, setSearch, status, setStatus };
  return (
    <div className="flex flex-col">
      <PageHeader title={t('customers.title')} subtitle={t('customers.subtitle')} />
      <div className="mb-3">
        <StepUpSettings />
      </div>
      <Tabs
        tabs={TABS.map((id) => ({ id, label: t(`customers.tabs.${id}`) }))}
        value={tab}
        onChange={setTab}
      />
      {tab === 'contacts' && <ContactsTab {...filters} />}
      {tab === 'companies' && <CompaniesTab {...filters} />}
      {tab === 'terms' && <TermsTab />}
      {tab === 'migration' && <MigrationTab />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Search and status filter                                             */
/* ------------------------------------------------------------------ */

function Chip({ active, label, count, onClick, title }: { active: boolean; label: string; count: number; onClick: () => void; title?: string }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      title={title}
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[13px] font-medium transition-colors',
        active ? 'border-accent bg-accent-soft text-accent' : 'border-line bg-sheet text-graphite hover:border-line-strong hover:text-ink',
      )}
    >
      {label}
      <span className="num text-xs opacity-80">{count}</span>
    </button>
  );
}

function FilterBar({ filters, counts, total, children }: { filters: Filters; counts: Map<CompanyStatus, number>; total: number; children?: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="mb-3 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-faint" />
          <Input className="pl-8" type="search" placeholder={t('customers.search')} value={filters.search} onChange={(e) => filters.setSearch(e.target.value)} />
        </div>
        {children}
      </div>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('customers.filterStatus')}>
        <Chip active={filters.status === ''} label={t('customers.allStatus')} count={total} onClick={() => filters.setStatus('')} />
        {COMPANY_STATUSES.map((s) => (
          <Chip key={s} active={filters.status === s} label={t(`customers.statuses.${s}`)} count={counts.get(s) ?? 0} onClick={() => filters.setStatus(filters.status === s ? '' : s)} />
        ))}
      </div>
    </div>
  );
}

function addressLine(r: { street: string | null; postalCode: string | null; city: string | null; country: string }): string {
  return [r.street, [r.postalCode, r.city].filter(Boolean).join(' '), r.country && r.country !== 'DE' ? r.country : ''].filter(Boolean).join(', ');
}

/* ------------------------------------------------------------------ */
/* Contacts: the table of the team                                      */
/* ------------------------------------------------------------------ */

const contactText = (c: ContactRow) => [c.companyName, c.street, c.postalCode, c.city, c.firstName, c.lastName, c.position, c.email, c.phone, c.note].filter(Boolean).join(' ');

function ContactsTab(filters: Filters) {
  const { t } = useTranslation();
  const { search, status } = filters;
  const [inactive, setInactive] = useSticky('customers.inactive', false);
  const q = useQ('customers.contacts', { includeInactive: inactive });
  const [edit, setEdit] = useState<ContactRow | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const exp = useContactsExport(search, status || undefined);
  const all = useMemo(() => q.data?.contacts ?? [], [q.data]);
  const searched = useMemo(() => all.filter((c) => matchesSearch(contactText(c), search)), [all, search]);
  const counts = useMemo(() => {
    const m = new Map<CompanyStatus, number>();
    for (const c of searched) m.set(c.status, (m.get(c.status) ?? 0) + 1);
    return m;
  }, [searched]);
  const sorting = useTableSort<ContactRow>('customers.contacts', {
    company: (c) => c.companyName,
    contact: (c) => `${c.lastName ?? ''} ${c.firstName ?? ''}`.trim(),
    email: (c) => c.email,
    status: (c) => c.status,
  });
  const rows = useMemo(() => sorting.apply(status ? searched.filter((c) => c.status === status) : searched), [searched, status, sorting.sort]); // eslint-disable-line react-hooks/exhaustive-deps

  if (q.isLoading) return <Loading />;
  return (
    <>
      <FilterBar filters={filters} counts={counts} total={searched.length}>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Checkbox label={t('customers.showInactive')} checked={inactive} onChange={(e) => setInactive(e.target.checked)} />
          <Button icon={<FileUp className="size-4" />} onClick={() => setImporting(true)}>
            {t('customers.importCsv')}
          </Button>
          <Button icon={<Download className="size-4" />} loading={exp.pending} disabled={rows.length === 0} onClick={exp.start}>
            {t('customers.exportCsv')}
          </Button>
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEdit('new')}>
            {t('customers.newContact')}
          </Button>
        </div>
      </FilterBar>
      <ErrorBox error={q.error} />
      {all.length === 0 ? (
        <EmptyState
          action={
            <div className="flex gap-2">
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEdit('new')}>
                {t('customers.newContact')}
              </Button>
              <Button icon={<FileUp className="size-4" />} onClick={() => setImporting(true)}>
                {t('customers.importCsv')}
              </Button>
            </div>
          }
        >
          {t('customers.emptyContacts')}
        </EmptyState>
      ) : rows.length === 0 ? (
        <EmptyState
          action={
            <Button
              onClick={() => {
                filters.setSearch('');
                filters.setStatus('');
              }}
            >
              {t('customers.resetFilters')}
            </Button>
          }
        >
          {t('customers.noMatches')}
        </EmptyState>
      ) : (
        <>
          <p className="mb-1.5 text-xs text-graphite">{t('customers.shownOf', { shown: rows.length, total: all.length })}</p>
          <VirtualTable
            rows={rows}
            rowKey={(r) => r.id}
            rowHeight={52}
            resetKey={`${search}|${status}|${sorting.sort?.key}|${sorting.sort?.dir}`}
            fixed
            minWidth={1100}
            label={t('customers.tabs.contacts')}
            head={
              <tr>
                <SortTh label={t('customers.cols.company')} sortKey="company" sort={sorting.sort} onSort={sorting.toggle} className="w-[24%]" />
                <SortTh label={t('customers.cols.contact')} sortKey="contact" sort={sorting.sort} onSort={sorting.toggle} className="w-56" />
                <SortTh label={t('customers.cols.email')} sortKey="email" sort={sorting.sort} onSort={sorting.toggle} className="w-60" />
                <th className={clsx(th, 'w-40')}>{t('customers.cols.phone')}</th>
                <SortTh label={t('customers.cols.status')} sortKey="status" sort={sorting.sort} onSort={sorting.toggle} className="w-44" />
                <th className={th}>{t('customers.cols.note')}</th>
              </tr>
            }
            rowProps={(r) => ({
              tabIndex: 0,
              onClick: () => setEdit(r),
              onKeyDown: (e) => {
                if (e.key === 'Enter') setEdit(r);
              },
              className: clsx('cursor-pointer hover:bg-sheet-2 focus-visible:bg-sheet-2 focus-visible:outline-none', !r.active && 'opacity-60'),
            })}
            renderRow={(r) => {
              const person = [r.firstName, r.lastName].filter(Boolean).join(' ');
              const address = addressLine(r);
              return (
                <>
                  <td className={cell}>
                    <span className={clsx(clip, 'font-medium')} title={r.companyName}>
                      {r.companyName}
                    </span>
                    <span className={clsx(clip, 'text-xs text-graphite')} title={address}>
                      {address}
                    </span>
                  </td>
                  <td className={cell}>
                    <span className={clip} title={person}>
                      {person}
                    </span>
                    <span className={clsx(clip, 'text-xs text-graphite')} title={r.position ?? ''}>
                      {r.position}
                    </span>
                  </td>
                  <td className={cell}>
                    {r.email &&
                      (r.status === 'do_not_contact' ? (
                        <span className={clsx(clip, 'text-graphite')} title={t('customers.dncHint')}>
                          {r.email}
                        </span>
                      ) : (
                        <a className={clsx(clip, 'text-accent hover:underline')} href={`mailto:${r.email}`} title={r.email} onClick={(e) => e.stopPropagation()}>
                          {r.email}
                        </a>
                      ))}
                  </td>
                  <td className={cell}>
                    <span className={clip} title={r.phone ?? ''}>
                      {r.phone}
                    </span>
                  </td>
                  <td className={cell}>
                    <span className="flex flex-wrap items-center gap-1">
                      <CompanyStatusPill status={r.status} />
                      {!r.active && <Pill>{t('customers.inactivePill')}</Pill>}
                    </span>
                  </td>
                  <td className={cell}>
                    <span className={clsx(clip, 'text-graphite')} title={r.note ?? ''}>
                      {r.note}
                    </span>
                  </td>
                </>
              );
            }}
          />
        </>
      )}
      <ContactDialog open={edit !== null} contact={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />
      <ImportDialog open={importing} onClose={() => setImporting(false)} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Companies                                                            */
/* ------------------------------------------------------------------ */

const companyText = (c: CompanyRow) => [c.name, c.legalName, c.city, c.customerNo, c.vatId, c.note, c.ownerName].filter(Boolean).join(' ');

function CompaniesTab(filters: Filters) {
  const { t } = useTranslation();
  const { search, status } = filters;
  const nav = useNavigate();
  const [archived, setArchived] = useSticky('customers.archived', false);
  const q = useQ('customers.companies', { includeArchived: archived });
  const [creating, setCreating] = useState(false);
  const all = useMemo(() => q.data?.companies ?? [], [q.data]);
  const searched = useMemo(() => all.filter((c) => matchesSearch(`${companyText(c)}`, search)), [all, search]);
  const counts = useMemo(() => {
    const m = new Map<CompanyStatus, number>();
    for (const c of searched) m.set(c.status, (m.get(c.status) ?? 0) + 1);
    return m;
  }, [searched]);
  const sorting = useTableSort<CompanyRow>('customers.companies', {
    company: (c) => c.name,
    city: (c) => c.city,
    status: (c) => c.status,
    owner: (c) => c.ownerName,
    contacts: (c) => c.contactCount,
    projects: (c) => c.projectCount,
    hours: (c) => c.hours12m,
    wip: (c) => c.wipCents,
    billed: (c) => c.billedCents,
  });
  const rows = useMemo(() => sorting.apply(status ? searched.filter((c) => c.status === status) : searched), [searched, status, sorting.sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const stats = q.data?.stats;
  const showHours = all.some((c) => c.hours12m !== null);
  const showWip = stats?.wipCents !== null && stats?.wipCents !== undefined;
  const showBilled = stats?.billedCents !== null && stats?.billedCents !== undefined;

  if (q.isLoading) return <Loading />;
  return (
    <>
      {stats && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Stat label={t('customers.stats.companies')} value={stats.total} />
          <Stat label={t('customers.stats.active')} value={stats.active} />
          <Stat label={t('customers.stats.prospects')} value={stats.prospects} hint={t('customers.stats.prospectsHint')} />
          {showWip && <Stat label={t('customers.stats.wip')} value={money(stats.wipCents)} hint={t('customers.stats.wipHint')} />}
          {showBilled && <Stat label={t('customers.stats.billed')} value={money(stats.billedCents)} hint={t('customers.stats.billedHint')} />}
        </div>
      )}
      <FilterBar filters={filters} counts={counts} total={searched.length}>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Checkbox label={t('customers.showArchived')} checked={archived} onChange={(e) => setArchived(e.target.checked)} />
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
            {t('customers.newCompany')}
          </Button>
        </div>
      </FilterBar>
      <ErrorBox error={q.error} />
      {all.length === 0 ? (
        <EmptyState action={<Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>{t('customers.newCompany')}</Button>}>{t('customers.emptyCompanies')}</EmptyState>
      ) : rows.length === 0 ? (
        <EmptyState
          action={
            <Button
              onClick={() => {
                filters.setSearch('');
                filters.setStatus('');
              }}
            >
              {t('customers.resetFilters')}
            </Button>
          }
        >
          {t('customers.noMatches')}
        </EmptyState>
      ) : (
        <>
          <p className="mb-1.5 text-xs text-graphite">{t('customers.shownOf', { shown: rows.length, total: all.length })}</p>
          <VirtualTable
            rows={rows}
            rowKey={(r) => r.id}
            rowHeight={48}
            resetKey={`${search}|${status}|${sorting.sort?.key}|${sorting.sort?.dir}`}
            fixed
            minWidth={showHours || showWip || showBilled ? 1250 : 950}
            label={t('customers.tabs.companies')}
            head={
              <tr>
                <SortTh label={t('customers.cols.company')} sortKey="company" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('customers.cols.city')} sortKey="city" sort={sorting.sort} onSort={sorting.toggle} className="w-44" />
                <SortTh label={t('customers.cols.status')} sortKey="status" sort={sorting.sort} onSort={sorting.toggle} className="w-44" />
                <SortTh label={t('customers.cols.owner')} sortKey="owner" sort={sorting.sort} onSort={sorting.toggle} className="w-40" />
                <SortTh label={t('customers.cols.contacts')} sortKey="contacts" sort={sorting.sort} onSort={sorting.toggle} className="w-24" right />
                <SortTh label={t('customers.cols.projects')} sortKey="projects" sort={sorting.sort} onSort={sorting.toggle} className="w-24" right />
                {showHours && <SortTh label={t('customers.cols.hours12m')} sortKey="hours" sort={sorting.sort} onSort={sorting.toggle} className="w-32" right />}
                {showWip && <SortTh label={t('customers.cols.wip')} sortKey="wip" sort={sorting.sort} onSort={sorting.toggle} className="w-36" right />}
                {showBilled && <SortTh label={t('customers.cols.billed')} sortKey="billed" sort={sorting.sort} onSort={sorting.toggle} className="w-36" right />}
              </tr>
            }
            rowProps={(r) => ({
              tabIndex: 0,
              onClick: () => nav(`/customers/${r.id}`),
              onKeyDown: (e) => {
                if (e.key === 'Enter') nav(`/customers/${r.id}`);
              },
              className: clsx('cursor-pointer hover:bg-sheet-2 focus-visible:bg-sheet-2 focus-visible:outline-none', r.archived && 'opacity-60'),
            })}
            renderRow={(r) => (
              <>
                <td className={cell}>
                  <span className={clsx(clip, 'font-medium')} title={r.name}>
                    {r.name}
                    {r.archived && <span className="ml-2 text-xs font-normal text-graphite">{t('customers.archivedPill')}</span>}
                  </span>
                  <span className={clsx(clip, 'text-xs text-graphite')} title={r.legalName ?? ''}>
                    {r.legalName ?? t(`customers.types.${r.type}`)}
                  </span>
                </td>
                <td className={cell}>
                  <span className={clip}>{[r.city, r.country !== 'DE' ? r.country : ''].filter(Boolean).join(', ')}</span>
                </td>
                <td className={cell}>
                  <CompanyStatusPill status={r.status} />
                </td>
                <td className={cell}>
                  <span className={clip}>{r.ownerName}</span>
                </td>
                <td className={clsx(cell, 'num text-right')}>{r.contactCount}</td>
                <td className={clsx(cell, 'num text-right')}>{r.projectCount}</td>
                {showHours && <td className={clsx(cell, 'num text-right')}>{r.hours12m === null ? '–' : hh(r.hours12m, 0)}</td>}
                {showWip && <td className={clsx(cell, 'num text-right')}>{money(r.wipCents)}</td>}
                {showBilled && <td className={clsx(cell, 'num text-right')}>{money(r.billedCents)}</td>}
              </>
            )}
          />
        </>
      )}
      <CompanyDialog open={creating} owners={q.data?.owners ?? []} onClose={() => setCreating(false)} onSaved={(id) => nav(`/customers/${id}`)} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Payment terms of the projects                                        */
/* ------------------------------------------------------------------ */

type TermsRow = ProcOutput<'customers.contracts'>['rows'][number];

function TermsTab() {
  const { t } = useTranslation();
  const [onlyOpen, setOnlyOpen] = useSticky('customers.termsOpen', false);
  const [search, setSearch] = useSticky('customers.termsSearch', '');
  const q = useQ('customers.contracts', { onlyOpen });
  const [open, setOpen] = useState<string | null>(null);
  const line = usePaymentLine();
  const all = useMemo(() => q.data?.rows ?? [], [q.data]);
  const rows = useMemo(() => all.filter((r) => matchesSearch(`${r.number} ${r.name} ${r.client ?? ''} ${r.recipient ?? ''}`, search)), [all, search]);
  const completeness = (r: TermsRow) => {
    if (!r.hasTerms) return <Pill tone="neutral">{t('customers.terms.none')}</Pill>;
    if (!r.gaps || r.gaps.length === 0) return <Pill tone="good">{t('customers.terms.complete')}</Pill>;
    return (
      <Pill tone="warn" title={r.gaps.map((g) => t(`customers.gaps.${g}`)).join(', ')}>
        {t('customers.terms.gapsCount', { count: r.gaps.length })}
      </Pill>
    );
  };

  if (q.isLoading) return <Loading />;
  return (
    <>
      <p className="mb-3 max-w-[80ch] text-sm text-graphite">{t('customers.terms.intro')}</p>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-faint" />
          <Input className="pl-8" type="search" placeholder={t('customers.terms.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Checkbox label={t('customers.terms.onlyOpen')} checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} />
      </div>
      <ErrorBox error={q.error} />
      {rows.length === 0 ? (
        <EmptyState>{all.length === 0 && onlyOpen ? t('customers.terms.allComplete') : t('customers.noMatches')}</EmptyState>
      ) : (
        <>
          <p className="mb-1.5 text-xs text-graphite">{t('customers.shownOf', { shown: rows.length, total: all.length })}</p>
          <VirtualTable
            rows={rows}
            rowKey={(r) => r.projectId}
            rowHeight={48}
            resetKey={`${search}|${onlyOpen}`}
            fixed
            minWidth={1150}
            label={t('customers.tabs.terms')}
            head={
              <tr>
                <th className={th}>{t('customers.cols.project')}</th>
                <th className={clsx(th, 'w-48')}>{t('customers.cols.client')}</th>
                <th className={clsx(th, 'w-48')}>{t('customers.cols.recipient')}</th>
                <th className={clsx(th, 'w-64')}>{t('customers.cols.paymentTerm')}</th>
                <th className={clsx(th, 'w-40')}>{t('customers.cols.method')}</th>
                <th className={clsx(th, 'w-36')}>{t('customers.cols.completeness')}</th>
                <th className={clsx(th, 'w-32 text-right')}>{t('customers.cols.items')}</th>
              </tr>
            }
            rowProps={(r) => ({
              tabIndex: 0,
              onClick: () => setOpen(r.projectId),
              onKeyDown: (e) => {
                if (e.key === 'Enter') setOpen(r.projectId);
              },
              className: 'cursor-pointer hover:bg-sheet-2 focus-visible:bg-sheet-2 focus-visible:outline-none',
            })}
            renderRow={(r) => (
              <>
                <td className={cell}>
                  <span className={clsx(clip, 'font-medium')} title={`${r.number} ${r.name}`}>
                    {r.number} {r.name}
                  </span>
                </td>
                <td className={cell}>
                  <span className={clip}>{r.client}</span>
                </td>
                <td className={cell}>
                  <span className={clip}>{r.recipient}</span>
                </td>
                <td className={cell}>
                  <span className={clip} title={line(r.paymentDays, r.discountPercent, r.discountDays)}>
                    {r.hasTerms ? line(r.paymentDays, r.discountPercent, r.discountDays) : '–'}
                  </span>
                </td>
                <td className={cell}>
                  <span className={clip}>{r.billingMethod ? t(`customers.methods.${r.billingMethod as 'hourly'}`) : '–'}</span>
                </td>
                <td className={cell}>{completeness(r)}</td>
                <td className={clsx(cell, 'num text-right')}>{r.itemsCents ? money(r.itemsCents) : '–'}</td>
              </>
            )}
          />
        </>
      )}
      {open && <TermsDialog projectId={open} onClose={() => setOpen(null)} />}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Takeover of the free texts "Kunde" and "Partner"                     */
/* ------------------------------------------------------------------ */

type Group = ProcOutput<'customers.migrationCandidates'>['groups'][number];

interface Pick {
  include: boolean;
  mode: 'new' | 'existing';
  name: string;
  companyId: string;
}

const groupKey = (g: Group) => `${g.field}|${g.names.map((n) => n.name).join('|')}`;

function MigrationTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const q = useQ('customers.migrationCandidates', {});
  const companies = useQ('customers.companies', {});
  const apply = useM('customers.applyMigration', { invalidate: ['customers.', 'projects.'] });
  const [picks, setPicks] = useState<Record<string, Partial<Pick>>>({});
  const groups = useMemo(() => q.data?.groups ?? [], [q.data]);
  const pickOf = (g: Group): Pick => ({
    include: false,
    mode: g.existingCompanyId ? 'existing' : 'new',
    name: g.suggestedName,
    companyId: g.existingCompanyId ?? '',
    ...picks[groupKey(g)],
  });
  const change = (g: Group, patch: Partial<Pick>) => setPicks((p) => ({ ...p, [groupKey(g)]: { ...p[groupKey(g)], ...patch } }));
  const chosen = groups.filter((g) => pickOf(g).include);
  const valid = chosen.every((g) => {
    const p = pickOf(g);
    return p.mode === 'new' ? p.name.trim().length > 0 : !!p.companyId;
  });
  const options = (companies.data?.companies ?? []).map((c) => ({ value: c.id, label: c.city ? `${c.name} (${c.city})` : c.name }));

  if (q.isLoading) return <Loading />;
  return (
    <>
      <p className="mb-3 max-w-[80ch] text-sm text-graphite">{t('customers.migration.intro')}</p>
      <ErrorBox error={q.error ?? apply.error} />
      {groups.length === 0 ? (
        <EmptyState>{t('customers.migration.none')}</EmptyState>
      ) : (
        <>
          <div className="mb-2 flex flex-wrap items-center gap-3">
            <Button onClick={() => setPicks(Object.fromEntries(groups.map((g) => [groupKey(g), { ...picks[groupKey(g)], include: true }])))}>{t('customers.migration.selectAll')}</Button>
            <Button variant="ghost" disabled={chosen.length === 0} onClick={() => setPicks({})}>
              {t('customers.migration.selectNone')}
            </Button>
            <span className="text-sm text-graphite">{t('customers.migration.selected', { count: chosen.length })}</span>
            <Button
              className="ml-auto"
              variant="primary"
              disabled={chosen.length === 0 || !valid}
              loading={apply.isPending}
              onClick={() =>
                apply.mutate(
                  {
                    groups: chosen.map((g) => {
                      const p = pickOf(g);
                      return p.mode === 'new'
                        ? { field: g.field, names: g.names.map((n) => n.name), mode: 'new' as const, name: p.name.trim() }
                        : { field: g.field, names: g.names.map((n) => n.name), mode: 'existing' as const, companyId: p.companyId };
                    }),
                  },
                  {
                    onSuccess: (r) => {
                      setPicks({});
                      toast.ok(t('customers.migration.applied', { created: r.created, linked: r.linked }));
                    },
                  },
                )
              }
            >
              {t('customers.migration.apply')}
            </Button>
          </div>
          <ScrollBox className="rounded-md border border-line bg-sheet" maxHeight="min(64vh, 40rem)" label={t('customers.tabs.migration')}>
            <ul className="divide-y divide-line/70">
              {groups.map((g) => {
                const p = pickOf(g);
                const projects = g.names.flatMap((n) => n.projects);
                const shown = projects.slice(0, 4);
                return (
                  <li key={groupKey(g)} className={clsx('grid gap-3 px-4 py-3 md:grid-cols-[1fr_minmax(0,22rem)]', p.include && 'bg-accent-soft/25')}>
                    <div className="min-w-0">
                      <Checkbox
                        label={
                          <span className="flex flex-wrap items-center gap-2">
                            <b className="text-[15px]">{g.suggestedName}</b>
                            <Pill tone="neutral">{t(g.field === 'customer' ? 'customers.migration.fieldCustomer' : 'customers.migration.fieldPartner')}</Pill>
                            <span className="text-xs text-graphite">{t('customers.migration.projectCount', { count: projects.length })}</span>
                          </span>
                        }
                        checked={p.include}
                        onChange={(e) => change(g, { include: e.target.checked })}
                      />
                      <p className="mt-1 ml-6 text-xs text-graphite">
                        {g.names.length > 1 && (
                          <>
                            {t('customers.migration.spellings')}: {g.names.map((n) => `„${n.name}“`).join(', ')}
                            <br />
                          </>
                        )}
                        {t('customers.migration.usedIn')}: {shown.map((x) => x.number).join(', ')}
                        {projects.length > shown.length ? ` +${projects.length - shown.length}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Select
                        value={p.mode}
                        onChange={(e) => change(g, { mode: e.target.value as Pick['mode'], include: true })}
                        options={[
                          { value: 'new', label: t('customers.migration.createNew') },
                          { value: 'existing', label: g.existingCompanyId ? `${t('customers.migration.linkExisting')} · ${t('customers.migration.suggested')}` : t('customers.migration.linkExisting') },
                        ]}
                      />
                      {p.mode === 'new' ? (
                        <Input value={p.name} maxLength={200} aria-label={t('customers.migration.shortName')} onChange={(e) => change(g, { name: e.target.value, include: true })} />
                      ) : (
                        <Select value={p.companyId} placeholder={t('customers.migration.pickCompany')} options={options} onChange={(e) => change(g, { companyId: e.target.value, include: true })} />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </ScrollBox>
        </>
      )}
    </>
  );
}
